import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { ArtistPortalArtistRef } from 'fym-shared/src/smartlinks/artistPortals';
import { TypedMemo } from '../../common/types';
import styles from './ArtistPortalArtistPicker.module.scss';
import { withBrandWord } from '../../music24/components/primitives/BrandWord';

interface ArtistPortalArtistRowProps {
  artist: ArtistPortalArtistRef;
  isLoading: boolean;
  isCurrent: boolean;
  onSelect: (externalArtistId: string) => void;
}

const ArtistPortalArtistRowComponent: React.FC<ArtistPortalArtistRowProps> = ({
  artist,
  isLoading,
  isCurrent,
  onSelect,
}) => {
  const intl = useIntl();
  const select = useCallback(() => onSelect(artist.externalId), [artist.externalId, onSelect]);

  return (
    <button type="button" className={styles.option} disabled={isLoading || isCurrent} onClick={select}>
      {artist.imageUrl !== null && <img src={artist.imageUrl} alt="" className={styles.pickerAvatar} />}
      <span>
        {artist.name}
        {isCurrent && (
          <span className={styles.currentNote}>
            {intl.formatMessage({ id: 'app.sml.catalog.connect.picker.current', defaultMessage: 'Connected now' })}
          </span>
        )}
      </span>
    </button>
  );
};

const ArtistPortalArtistRow = TypedMemo(ArtistPortalArtistRowComponent);

interface ArtistPortalArtistPickerProps {
  artists: ArtistPortalArtistRef[];
  isLoading: boolean;
  onSelect: (externalArtistId: string) => void;
  /** Replaces the help line above the list. */
  help?: string;
  /** The artist the connection reads now. Its row says so and cannot be picked again. */
  currentExternalId?: string | null;
}

/**
 * One sign-in can hold several artists. A connection row holds one, so the
 * artist says which before any sync runs.
 */
const ArtistPortalArtistPickerComponent: React.FC<ArtistPortalArtistPickerProps> = ({
  artists,
  isLoading,
  onSelect,
  help,
  currentExternalId,
}) => {
  const intl = useIntl();

  return (
    <div>
      <p className={styles.sheetNote}>
        {withBrandWord(help) ??
          withBrandWord(
            intl.formatMessage({
              id: 'app.sml.catalog.connect.picker.help',
              defaultMessage: 'This sign-in holds more than one artist. Pick the one MUSIC24 should read.',
            })
          )}
      </p>
      <div className={styles.options}>
        {artists.map((artist) => (
          <ArtistPortalArtistRow
            key={artist.externalId}
            artist={artist}
            isLoading={isLoading}
            isCurrent={currentExternalId !== undefined && currentExternalId === artist.externalId}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
};

export const ArtistPortalArtistPicker = TypedMemo(ArtistPortalArtistPickerComponent);
